/**
 * Mapa de atalhos de teclado (FRONTEND_GUIDE 6).
 *
 * Aqui só existem dados e funções puras: quais teclas o jogo escuta, o que cada
 * uma faz e como a tela de ajuda deve listá-las. Quem escuta o teclado é o
 * gancho de `useAtalhos.ts`; quem executa cada ação é a tela que monta o gancho.
 *
 * A lista mora em um lugar só de propósito: a ajuda é gerada a partir dela, então
 * atalho novo aparece na ajuda sem ninguém lembrar de escrever a linha lá.
 *
 * O texto não mora aqui: cada atalho guarda a chave do i18n e quem desenha a
 * ajuda é que traduz, então esta lista continua sendo só dados.
 */
import type { Chave } from "../i18n";

/** Tudo que uma tecla pode pedir. A tela decide como fazer. */
export type AcaoDeAtalho =
  | "jogar"
  | "elenco"
  | "tatica"
  | "mercado"
  | "clube"
  | "competicoes"
  | "noticias"
  | "financas"
  | "carreira"
  | "busca"
  | "ajuda"
  | "voltar"
  | "listaAcima"
  | "listaAbaixo"
  | "listaAbrir"
  | "assento";

/**
 * Onde o atalho vale.
 *
 * `hub` só dispara com o hub do clube na frente, sem nenhum diálogo aberto.
 * `dialogo` é documentação: quem trata essas teclas é o próprio diálogo, que as
 * registra ao abrir. A regra é que diálogo aberto silencia o resto do jogo.
 */
export type EscopoDeAtalho = "hub" | "dialogo";

export interface Atalho {
  /** Teclas como a ajuda desenha, uma por tampa. */
  teclas: string[];
  /** Palavra entre as tampas na ajuda, quando são uma faixa ("1 a 9"). */
  ligacao?: Chave;
  /** Tecla normalizada para a ação que ela dispara. */
  acoes: Partial<Record<string, AcaoDeAtalho>>;
  /** Chave do i18n com a descrição que a ajuda mostra. */
  descricao: Chave;
  escopo: EscopoDeAtalho;
  /** Chave do i18n com o título do bloco na tela de ajuda. */
  grupo: Chave;
  /** Atalho previsto pelo guia mas sem tela por trás ainda. */
  emBreve?: boolean;
}

/**
 * Nomes internos das teclas que não são uma letra.
 *
 * Existe para o resto do código falar em "cima" e "esc" em vez de repetir os
 * nomes do DOM, que mudam de maiúscula conforme a tecla.
 */
const NOMES_DE_TECLA: Record<string, string> = {
  arrowup: "cima",
  arrowdown: "baixo",
  arrowleft: "esquerda",
  arrowright: "direita",
  escape: "esc",
  " ": "espaco",
  spacebar: "espaco",
};

/** Tecla do evento no formato que este módulo usa. */
export function normalizarTecla(evento: KeyboardEvent): string {
  const bruta = evento.key.toLowerCase();
  return NOMES_DE_TECLA[bruta] ?? bruta;
}

/**
 * O foco está em um campo de digitação.
 *
 * Regra que não pode falhar: quem está escrevendo o nome de um jogador na busca
 * digita "j" e "e" sem que a rodada seja jogada e a tela pule para o elenco.
 */
export function ehCampoDeTexto(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (alvo.isContentEditable) return true;
  const etiqueta = alvo.tagName.toLowerCase();
  return etiqueta === "input" || etiqueta === "textarea" || etiqueta === "select";
}

/**
 * O foco está em algo que o próprio navegador aciona com Enter ou espaço.
 *
 * Sem isto, apertar Enter com um botão focado dispararia o botão e o atalho ao
 * mesmo tempo, que é o tipo de duplicidade que faz o jogador perder uma rodada.
 */
export function ehAcionavelPeloNavegador(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  const etiqueta = alvo.tagName.toLowerCase();
  return etiqueta === "button" || etiqueta === "a" || alvo.getAttribute("role") === "button";
}

/** Teclas que o navegador usa para acionar o elemento em foco. */
export function ehTeclaDeAcionamento(tecla: string): boolean {
  return tecla === "enter" || tecla === "espaco";
}

/**
 * A lista de atalhos, na ordem em que a ajuda mostra.
 *
 * As letras de seção vêm do guia: E de Elenco, T de Tática, M de Mercado, C de
 * Clube, P de comPetições, N de Notícias. Finanças ganhou F porque é tela que o
 * guia ainda não tinha quando a tabela foi escrita, e deixá-la sem tecla
 * quebraria a promessa de jogar a rodada sem tocar no mouse.
 *
 * O A de assistir saiu quando assistir virou a mesma coisa que jogar a rodada.
 */
export const ATALHOS: Atalho[] = [
  {
    teclas: ["J"],
    acoes: { j: "jogar" },
    descricao: "atalhos.descricoes.jogar",
    escopo: "hub",
    grupo: "atalhos.grupos.rodada",
  },
  {
    teclas: ["E"],
    acoes: { e: "elenco" },
    descricao: "atalhos.descricoes.elenco",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["T"],
    acoes: { t: "tatica" },
    descricao: "atalhos.descricoes.tatica",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["M"],
    acoes: { m: "mercado" },
    descricao: "atalhos.descricoes.mercado",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["C"],
    acoes: { c: "clube" },
    descricao: "atalhos.descricoes.clube",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["P"],
    acoes: { p: "competicoes" },
    descricao: "atalhos.descricoes.competicoes",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["N"],
    acoes: { n: "noticias" },
    descricao: "atalhos.descricoes.noticias",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["F"],
    acoes: { f: "financas" },
    descricao: "atalhos.descricoes.financas",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["R"],
    acoes: { r: "carreira" },
    descricao: "atalhos.descricoes.carreira",
    escopo: "hub",
    grupo: "atalhos.grupos.secoes",
  },
  {
    teclas: ["/"],
    acoes: { "/": "busca" },
    descricao: "atalhos.descricoes.busca",
    escopo: "hub",
    grupo: "atalhos.grupos.navegacao",
  },
  {
    teclas: ["↑", "↓"],
    acoes: { cima: "listaAcima", baixo: "listaAbaixo" },
    descricao: "atalhos.descricoes.lista",
    escopo: "hub",
    grupo: "atalhos.grupos.navegacao",
  },
  {
    teclas: ["Enter"],
    acoes: { enter: "listaAbrir" },
    descricao: "atalhos.descricoes.abrir",
    escopo: "hub",
    grupo: "atalhos.grupos.navegacao",
  },
  {
    teclas: ["Esc"],
    acoes: { esc: "voltar" },
    descricao: "atalhos.descricoes.voltar",
    escopo: "hub",
    grupo: "atalhos.grupos.navegacao",
  },
  {
    teclas: ["?"],
    acoes: { "?": "ajuda" },
    descricao: "atalhos.descricoes.ajuda",
    escopo: "hub",
    grupo: "atalhos.grupos.navegacao",
  },
  {
    teclas: ["1", "9"],
    ligacao: "atalhos.ligacaoAte",
    acoes: {
      "1": "assento",
      "2": "assento",
      "3": "assento",
      "4": "assento",
      "5": "assento",
      "6": "assento",
      "7": "assento",
      "8": "assento",
      "9": "assento",
    },
    descricao: "atalhos.descricoes.assento",
    escopo: "hub",
    grupo: "atalhos.grupos.hotseat",
  },
  {
    teclas: ["Espaço"],
    acoes: {},
    descricao: "atalhos.descricoes.pausar",
    escopo: "dialogo",
    grupo: "atalhos.grupos.aoVivo",
  },
  {
    teclas: ["1", "2", "3"],
    acoes: {},
    descricao: "atalhos.descricoes.ritmo",
    escopo: "dialogo",
    grupo: "atalhos.grupos.aoVivo",
  },
  {
    teclas: ["Esc"],
    acoes: {},
    descricao: "atalhos.descricoes.pular",
    escopo: "dialogo",
    grupo: "atalhos.grupos.aoVivo",
  },
  {
    teclas: ["Esc"],
    acoes: {},
    descricao: "atalhos.descricoes.fecharPainel",
    escopo: "dialogo",
    grupo: "atalhos.grupos.painelAberto",
  },
  {
    teclas: ["Enter"],
    acoes: {},
    descricao: "atalhos.descricoes.continuar",
    escopo: "dialogo",
    grupo: "atalhos.grupos.painelAberto",
  },
  {
    teclas: ["C"],
    acoes: {},
    descricao: "atalhos.descricoes.cartao",
    escopo: "dialogo",
    grupo: "atalhos.grupos.painelAberto",
  },
];

/** Ação do hub para uma tecla, ou nulo quando a tecla não é atalho. */
export function acaoDoHub(tecla: string): AcaoDeAtalho | null {
  for (const atalho of ATALHOS) {
    if (atalho.escopo !== "hub" || atalho.emBreve) continue;
    const acao = atalho.acoes[tecla];
    if (acao) return acao;
  }
  return null;
}

/** Atalhos agrupados na ordem da lista, para a tela de ajuda desenhar. */
export function atalhosPorGrupo(): Array<{ grupo: Chave; itens: Atalho[] }> {
  const grupos: Array<{ grupo: Chave; itens: Atalho[] }> = [];
  for (const atalho of ATALHOS) {
    const ultimo = grupos.find((entrada) => entrada.grupo === atalho.grupo);
    if (ultimo) ultimo.itens.push(atalho);
    else grupos.push({ grupo: atalho.grupo, itens: [atalho] });
  }
  return grupos;
}

// ---------------------------------------------------------------------------
// Navegação de lista
// ---------------------------------------------------------------------------

/**
 * Setas e Enter andando pelas listas da tela.
 *
 * O jogo tem lista em quase toda seção (elenco, mercado, classificação,
 * artilharia) e todas nascem da mesma linha clicável. Em vez de cada painel
 * carregar um índice próprio no estado do React, a marca vive no atributo da
 * linha: funciona em qualquer tabela que já exista e nas que vierem depois,
 * sem uma linha de código por painel.
 */
const SELETOR_DE_LINHA = "tr.player";

/** Atributo que marca a linha escolhida pelo teclado. O CSS pinta por ele. */
export const MARCA_DE_LINHA = "data-tecla-ativa";

function linhasVisiveis(): HTMLElement[] {
  const area = document.querySelector(".main") ?? document.body;
  return Array.from(area.querySelectorAll<HTMLElement>(SELETOR_DE_LINHA)).filter(
    (linha) => linha.offsetParent !== null,
  );
}

/** Move a marca para cima ou para baixo. Devolve se havia lista para andar. */
export function moverNaLista(passo: number): boolean {
  const linhas = linhasVisiveis();
  if (linhas.length === 0) return false;

  const atual = linhas.findIndex((linha) => linha.hasAttribute(MARCA_DE_LINHA));
  const alvoIndice =
    atual < 0
      ? passo > 0
        ? 0
        : linhas.length - 1
      : Math.min(linhas.length - 1, Math.max(0, atual + passo));

  const alvo = linhas[alvoIndice];
  if (!alvo) return false;

  for (const linha of linhas) linha.removeAttribute(MARCA_DE_LINHA);
  alvo.setAttribute(MARCA_DE_LINHA, "sim");
  alvo.scrollIntoView({ block: "nearest" });
  return true;
}

/** Abre a linha marcada, como se o jogador tivesse clicado nela. */
export function abrirLinhaMarcada(): boolean {
  const alvo = document.querySelector<HTMLElement>(
    `.main ${SELETOR_DE_LINHA}[${MARCA_DE_LINHA}]`,
  );
  if (!alvo) return false;
  alvo.click();
  return true;
}

/** Nome do atributo que a busca do mercado usa para receber o foco do "/". */
export const MARCA_DE_BUSCA = "data-busca-de-jogador";

/**
 * Põe o cursor na busca do mercado.
 *
 * O painel só existe no DOM depois que o React desenha a seção nova, por isso a
 * espera de dois quadros em vez de um foco direto.
 */
export function focarBusca(): void {
  let tentativas = 0;
  const tentar = (): void => {
    const campo = document.querySelector<HTMLInputElement>(`[${MARCA_DE_BUSCA}]`);
    if (campo) {
      campo.focus();
      campo.select();
      return;
    }
    tentativas += 1;
    if (tentativas < 10) window.requestAnimationFrame(tentar);
  };
  window.requestAnimationFrame(tentar);
}
